import { JobListItem } from '../JobList/jobListItem'
import { acceptsDrops, closedReasonFor, isUnLosing, KanbanColumn } from './jobKanbanModel'

export type PendingMove = {
  job: JobListItem
  from: number
  to: number
}

export type ColumnContents = {
  jobs: JobListItem[]
  totalRows: number
  totalFinalPrice: number
}

export const columnDroppableId = (status: number) => `job-kanban-column-${status}`

export const statusOf = (job: JobListItem, sold: boolean) => sold ? job.status : job.estimateStatus

export const effectiveStatusOf = (job: JobListItem, sold: boolean, moves: PendingMove[]) => {
  const pending = moves.find(move => move.job.id === job.id)
  return pending ? pending.to : statusOf(job, sold)
}

export const canDropInto = (
  columns: KanbanColumn[], targetStatus: number, currentStatus: number, sold = true
) => targetStatus !== currentStatus
  && (acceptsDrops(columns, targetStatus) || isUnLosing(sold, currentStatus, targetStatus))

export const undoTargetFor = (job: JobListItem | undefined, moves: PendingMove[]) =>
  job ? moves.find(move => move.job.id === job.id)?.from : undefined

export const addPendingMove = (moves: PendingMove[], job: JobListItem, from: number, to: number): PendingMove[] => {
  const existing = moves.find(move => move.job.id === job.id)
  const origin = existing ? existing.from : from
  const others = moves.filter(move => move.job.id !== job.id)
  if (origin === to) {
    return others
  }
  return [...others, { job, from: origin, to }]
}

export type DropResolution = {
  moved: boolean
  moves: PendingMove[]
  job?: JobListItem
  to?: number
  undone?: boolean
  /* Why a drop was turned away, when there is something worth saying. Dropping outside any
     column, or back into the column the card already sits in, is not worth explaining —
     the user did not ask for anything. */
  refusedReason?: string
}

export const resolveDrop = (
  columns: KanbanColumn[],
  sold: boolean,
  moves: PendingMove[],
  active: { job?: JobListItem, from?: number },
  over: { status?: number }
): DropResolution => {
  const { job, from } = active
  const to = over.status

  if (!job || from === undefined || to === undefined) {
    return { moved: false, moves }
  }

  const currentStatus = effectiveStatusOf(job, sold, moves)
  const undoing = undoTargetFor(job, moves) === to
  if (!undoing && !canDropInto(columns, to, currentStatus, sold)) {
    return {
      moved: false,
      moves,
      job,
      to,
      refusedReason: to === currentStatus ? undefined : closedReasonFor(columns, to)
    }
  }

  return { moved: true, moves: addPendingMove(moves, job, from, to), job, to, undone: undoing }
}

export const removePendingMove = (moves: PendingMove[], jobId: number) =>
  moves.filter(move => move.job.id !== jobId)

/* JobSnapshots, which the board reads, is rebuilt by a listener after the status write
   returns, so a refetch can still report the old column. A move is therefore held until
   the source column stops reporting the job — the one signal that means the read model
   has caught up. The job was in that column's loaded page when it was dragged, so its
   absence is real rather than a paging artefact.
   Preview moves are never confirmed this way, because nothing was written and the source
   column keeps reporting the job, which is exactly what should keep the preview in place. */
export const movesConfirmedBy = (
  status: number,
  serverJobs: JobListItem[],
  moves: PendingMove[]
) => {
  const present = new Set(serverJobs.map(job => job.id))
  return moves
    .filter(move => move.from === status && !present.has(move.job.id))
    .map(move => move.job.id)
}

const sumFinalPrice = (jobs: JobListItem[]) => jobs.reduce((total, job) => total + job.finalPrice, 0)

export const applyPendingMoves = (contents: ColumnContents, status: number, moves: PendingMove[]): ColumnContents => {
  const movedAway = moves.filter(move => move.from === status && move.to !== status)
  const movedAwayIds = new Set(movedAway.map(move => move.job.id))
  const kept = contents.jobs.filter(job => !movedAwayIds.has(job.id))

  const keptIds = new Set(kept.map(job => job.id))
  const incoming = moves
    .filter(move => move.to === status && !keptIds.has(move.job.id))
    .map(move => move.job)

  return {
    jobs: [...incoming, ...kept],
    totalRows: Math.max(0, contents.totalRows - movedAway.length + incoming.length),
    totalFinalPrice: contents.totalFinalPrice - sumFinalPrice(movedAway.map(move => move.job)) + sumFinalPrice(incoming)
  }
}
